import { List, Map, Set } from 'immutable';

import { canBeScheduled, canChangeStatuses, filterStatusAndScheduleOperations } from '../utils/product/statuses';
import { addProfileId, shapeNewLinkedProductsForAPI, shapeNewProductForAPI } from '../utils/product/shapeForAPI';
import { updateItemSpecificsForCategory, updateProductDetailsForCategory } from '../utils/product/taxonomy';
import { shapeLinkedProductsOperations, shapeProductOperations } from '../utils/product/shapeOperations';
import { shapeLinkedProductForApp, shapeLinkedProductForAPI } from '../utils/product/linkedProducts';
import { shapeProductForApp, sortShops } from '../utils/product/shapeForApp';
import { shapeAiOperationsForApp } from '../utils/profiles/ai';
import { shouldAddPlaceholder } from '../utils/product/shouldAddPlaceholder';
import { getConnectedShopIds } from '../utils/product/getConnectedShopIds';
import { shapeListingsForApp } from '../utils/profiles/listings';
import { updateListingScore } from '../utils/product/score';
import { setProductErrors } from '../utils/product/errors';
import { createController } from '../utils/controller';
import { generateKeywords } from '../utils/ai';
import { getListingsPath } from '../utils/listings/getListingsPath';
import { canConnectShop } from '../utils/product/canConnectShop';
import { editNewProduct } from '../utils/product/editNewProduct';
import { getPlaceholder } from '../utils/listings/placeholders';
import { shapeForTable } from '../utils/product/shapeForTable';
import { applyProfile } from '../utils/product/applyProfile';
import { isVelaStatus } from '../utils/listings/statuses';
import { editKeywords } from '../utils/edit/keywords';
import { areComplete } from '../utils/product/isComplete';
import { navigateTo } from '../utils/navigation';
import { shapeId } from '../utils/listings/listings';
import { getSize } from '../utils/iterable/getSize';
import { get } from '../utils/iterable/get';
import api from '../utils/api';

import { AMOUNT, ENDPOINTS, ERROR, NEW, VALUE } from '../constants/product';
import { PAGE_SIZE, PROFILE_TYPE } from '../constants/profiles';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { POLLING_INTERVAL } from '../constants/shops';
import { SCHEDULE_ACTION } from '../constants/schedule';
import { EBAY, SHOPIFY } from '../constants/channels';
import { EMPTY, VELA } from '../constants';
import { ATTRIBUTE } from '../constants/attributes';
import { STATUS } from '../constants/listings';
import { MODALS } from '../constants/modal';
import { FORM } from '../constants/forms';
import { DATA } from '../data';

import actions from '../actions';

const connectedProductsController = createController();
const productsController = createController();
const profileListingsController = createController();
const profileController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    connectedProductsController,
    productsController,
    profileListingsController,
    profileController,
  ].forEach(abort);
}

function* addPlaceholdersForNewListings(reduction, { data, shopIds, status }) {
  yield function effect(dispatch) {
    const shops = Set(data.get('shops'));

    function addPlaceholder(shopId) {
      const product = data.getIn(['products', shopId]);

      if (!product) return;

      dispatch(
        actions.listings.addListingPlaceholder({
          placeholder: getPlaceholder({
            channel: product.get('channel'),
            product: shapeForTable({
              product,
              shopData: reduction.getIn(['data', 'shopsData', shopId]),
              shopIds: shops.delete(shopId).toList(),
              userShops: reduction.get('shops'),
            }),
          }),
          shopId,
          status,
        })
      );
    }

    shopIds.forEach(addPlaceholder);
  };

  return reduction;
}

function* cancelEdit(reduction) {
  abortAll();

  if (!reduction.hasIn(['edit', 'session'])) {
    return reduction;
  }

  let edit = EMPTY.MAP;

  if (reduction.getIn(['edit', 'processing'])) {
    edit = edit.set('processing', reduction.getIn(['edit', 'processing']));
  }

  if (reduction.hasIn(['edit', 'syncStarted'])) {
    edit = edit.set('syncStarted', reduction.getIn(['edit', 'syncStarted']));
  }

  return reduction.set('edit', edit);
}

function* createNew(reduction, { product, shopId }) {
  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const userShops = reduction.get('shops');

  if (!userShops.hasIn(['byId', shopId])) return reduction;

  let edit = editNewProduct({
    data: reduction.get('edit').deleteAll(['processing', 'reloadAfterSave', 'session', 'syncStarted']),
    product,
    shopId,
    userFeatures,
    userShops,
  });

  yield function effect(dispatch) {
    dispatch(actions.edit.getData());
  };

  if (reduction.hasIn(['edit', 'processing'])) {
    edit = edit.set('processing', reduction.getIn(['edit', 'processing']));
  }

  if (reduction.hasIn(['edit', 'syncStarted'])) {
    edit = edit.set('syncStarted', reduction.getIn(['edit', 'syncStarted']));
  }

  if (reduction.hasIn(['edit', 'reloadAfterSave'])) {
    edit = edit.set('reloadAfterSave', reduction.getIn(['edit', 'reloadAfterSave']));
  }

  return reduction.set('edit', edit.set('session', reduction.getIn(['edit', 'session'])));
}

function* getData(reduction) {
  yield function effect(dispatch) {
    reduction.getIn(['edit', 'shops']).forEach(function getAllData(shopId) {
      dispatch(actions.data.getAllData(shopId));
    });
  };

  return reduction;
}

function* getLinkedProducts(reduction, { db, linkId, productId, shopId, signal: productsSignal }) {
  abort(connectedProductsController);

  yield function effect(dispatch) {
    const session = reduction.getIn(['edit', 'session']);
    const { signal } = connectedProductsController.start();
    api.products.getLinkedProducts({ db, linkId, productId, signal })
      .then(
        (products) => {
          dispatch(actions.edit.getLinkedProducts.fulfilled({ products, session, shopId, signal: productsSignal }));
        },
        (error) => dispatch(actions.edit.getLinkedProducts.rejected({ error, session, signal }))
      );
  };

  return reduction;
}

function* getLinkedProductsFulfilled(reduction, { products, session, shopId, signal }) {
  if (reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  abort(connectedProductsController);
  const user = reduction.get('user');
  const userShops = reduction.get('shops');
  const linkedProducts = shapeLinkedProductForApp({ products, user, userShops });
  let edit = reduction.get('edit');

  if (getSize(linkedProducts) === 0 && getSize(edit.get('shops')) === 1) return reduction;

  function reduceProducts(result, linkedProduct) {
    result.shops = result.shops.push(linkedProduct.get('shopId'));
    result.actions.push(
      actions.edit.getProductData({
        channel: userShops.getIn(['byId', linkedProduct.get('shopId'), 'channel']),
        db: userShops.getIn(['byId', linkedProduct.get('shopId'), 'db']),
        productId: linkedProduct.get('productId'),
        shopId: linkedProduct.get('shopId'),
        signal,
      })
    );

    return result;
  }

  const {
    actions: actionsToDispatch,
    shops,
  } = linkedProducts.reduce(reduceProducts, { actions: [], shops: List([shopId]) });

  if (getSize(actionsToDispatch) > 0) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  edit = edit
    .set('linkedProducts', linkedProducts)
    .set('shops',
      shops.sort(
        sortShops({
          first: edit.get('shopId'),
          prioritized: getConnectedShopIds(edit.get('products')),
          sorted: userShops.get('options'),
        })
      )
    );

  if (edit.has('activeShopId') && !edit.get('shops').includes(edit.get('activeShopId'))) {
    edit = edit.set('activeShopId', edit.get('shopId'));
  }

  return reduction.set('edit', edit);
}

function* getLinkedProductsRejected(reduction, { error, session, signal }) {
  if (signal.aborted || reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  abort(connectedProductsController);

  yield function effect() {
    console.error('Error encountered while getting linked product: ', error);
    navigateTo(getListingsPath(reduction));
  };

  return reduction.set('edit', EMPTY.MAP);
}

function* getProduct(reduction, action = {}) {
  abort(connectedProductsController);
  abort(productsController);

  const {
    productId = reduction.getIn(['edit', 'productId']),
    shopId = reduction.getIn(['edit', 'shopId']),
  } = action;

  const shop = reduction.getIn(['shops', 'byId', shopId]);

  if (!productId || !shop) {
    yield function effect(dispatch) {
      dispatch(actions.edit.cancelEdit());
    };

    return reduction;
  }

  const db = shop.get('db');
  const channel = shop.get('channel');
  const linkId = reduction.getIn(['edit', 'linkId']);
  const { signal } = productsController.start();

  yield function effect(dispatch) {
    dispatch(actions.edit.getProductData({ channel, db, productId, shopId, signal }));

    if (linkId) {
      dispatch(actions.edit.getLinkedProducts({ db, linkId, productId, shopId, signal }));
    }

    if (getSize(reduction.getIn(['edit', 'shops'])) > 0) {
      dispatch(actions.edit.getData());
    }
  };

  return reduction
    .deleteIn(['edit', 'linkedProducts'])
    .deleteIn(['edit', 'reloadAfterSave']);
}

function* getProductData(reduction, { asProfile, channel, db, productId, shopId, signal }) {
  yield function effect(dispatch) {
    dispatch(actions.data.getAllData(shopId));

    const session = reduction.getIn(['edit', 'session']);

    api.products
      .getById({ channel, db, payload: { id: [productId] }, shopId, signal })
      .then(
        (response) => {
          dispatch(
            actions.edit.getProductData.fulfilled({
              asProfile,
              channel,
              db,
              productId,
              response,
              session,
              shopId,
              signal,
            })
          );
        },
        (error) => {
          if (asProfile) {
            dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal }));
          } else {
            dispatch(actions.edit.getProductData.rejected({ error, session, signal }));
          }
        }
      );
  };

  return reduction;
}

function* getProductDataFulfilled(reduction, { asProfile, channel, db, productId, response, session, shopId, signal }) {
  if (reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  let data = reduction.get('edit');

  if (!data.get('productId')) return reduction;

  if (!getSize(response?.products)) {
    yield function effect(dispatch) {
      const error = `Product "${productId}" was not found in the shop "${shopId}" at DB "${db}"`;

      if (asProfile) {
        dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal }));
      } else {
        dispatch(actions.edit.getProductData.rejected({ error, session, signal }));
      }
    };

    return reduction;
  }

  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const marketplaceId = reduction.getIn(['shops', 'byId', shopId, 'marketplaceId']);
  const taxonomyData = get([channel, marketplaceId])(DATA);
  const titleFormatPreference = reduction.getIn(['shops', 'byId', shopId, 'titleFormatPreference']);
  let product;

  try {
    product = shapeProductForApp({
      asProfile,
      channel,
      data: response,
      shopData, shopId,
      taxonomyData,
      titleFormatPreference,
      userFeatures,
    });
  } catch (error) {
    yield function effect(dispatch) {
      if (asProfile) {
        dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal }));
      } else {
        dispatch(actions.edit.getProductData.rejected({ error, session, signal }));
      }
    };

    return reduction;
  }

  if (channel === EBAY) {
    const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

    if (
      category &&
      category !== AMOUNT.ZERO &&
      !reduction.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category])
    ) {
      product = product.set('loading', true);

      yield function effect(dispatch) {
        dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
      };
    }
  }

  function updateStatuses(statuses = EMPTY.MAP) {
    return statuses.set(shopId, product.get('status'));
  }

  const userShops = reduction.get('shops');

  if (asProfile) {
    const profile = reduction.getIn(['edit', 'products', shopId, 'profile']);

    if (channel === EBAY && profile.getIn(['sections', FORM.DETAILS])) {
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

      if (!reduction.hasIn(['data', 'loaded', 'taxonomy', channel, marketplaceId, category])) {
        yield function effect(dispatch) {
          dispatch(actions.data.getTaxonomyData({ category, channel, marketplaceId }));
        };

        return reduction
          .setIn(['edit', 'products', shopId, 'profile', 'template'], product)
          .setIn(['edit', 'waitingForCategoriesToLoad', shopId], Set([category]));
      }
    }

    yield function effect(dispatch) {
      dispatch(actions.edit.setProfile.fulfilled({ session, shopId, type: PROFILE_TYPE.STANDARD }));
    };

    return reduction.setIn(['edit', 'products', shopId, 'profile', 'template'], product);
  } else {
    const currentShopId = data.get('shopId');
    const user = reduction.get('user');

    if (canConnectShop({ connectedShops: data.get('shops', EMPTY.LIST), user, userShops })(shopId)) {
      data = data.set('shops',
        data
          .get('shops', EMPTY.LIST)
          .push(shopId)
          .sort(
            sortShops({
              fitst: currentShopId,
              prioritized: getConnectedShopIds(data.get('products')).add(shopId),
              sorted: userShops.get('options'),
            })
          )
      );
    }

    if (shopId === currentShopId && data.get('loading')) {
      if (product.get('linkId') && !data.get('linkId')) {
        data = data.set('shops',
          data
            .get('shops', EMPTY.LIST)
            .concat(
              product
                .get('linkedProductsShopIds')
                .filter(canConnectShop({ connectedShops: data.get('shops', EMPTY.LIST), user, userShops }))
            )
            .sort(
              sortShops({
                first: currentShopId,
                prioritized: getConnectedShopIds(data.get('products')).add(shopId),
                sorted: userShops.get('options'),
              })
            )
        );

        if (getSize(data.get('shops')) > 1) {
          yield function effect(dispatch) {
            dispatch(
              actions.edit.getLinkedProducts({
                db,
                linkId: data.get('linkId'),
                productId: data.get('productId'),
                shopId,
                signal,
              })
            );
          };
        }
      }

      data = data
        .delete('loading')
        .set('title', product.getIn([ATTRIBUTE.TITLE, VALUE]))
        .set('status', product.get('status'))
        .set('channelProductId', product.get('channelProductId'))
        .set('linkId', product.get('linkId'))
        .set('channel', product.get('channel'))
        .set('isComplete', !getSize(product.get(ERROR)))
        .set('scheduledAt', product.get('scheduledAt'))
        .set('thumbnailUrl', product.getIn([ATTRIBUTE.PHOTOS, VALUE, 0, 'fullsize_url']));
    }

    if (getSize(data.get('shops')) === getSize(data.get('products', EMPTY.MAP).delete(shopId)) + 1) {
      abort(productsController);
    }
  }

  data = data
    .setIn(['products', shopId], product)
    .update('statuses', updateStatuses);

  return reduction.set('edit', data.set('isComplete', areComplete(data.get('products'))));
}

function* getProductDataRejected(reduction, { error, session, signal }) {
  console.error('Error encountered while getting product: ', error);

  if (signal.aborted || reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  if (!reduction.getIn(['edit', 'processing'])) {
    yield function effect() {
      navigateTo(getListingsPath(reduction));
    };

    return reduction.set('edit', EMPTY.MAP);
  }

  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getProfileListings(state, shopId) {
  abort(profileListingsController);

  if (!shopId || !state.hasIn(['edit', 'products', shopId, 'profile'])) return state;

  const profile = state.getIn(['edit', 'products', shopId, 'profile']);
  const profileId = profile.get('id');
  const listings = profile.get('listings');
  const page = listings.get('page', -1) + 1;
  const limit = PAGE_SIZE;
  const offset = page * limit;

  if (offset >= listings.get('total')) return state;

  yield function effect(dispatch) {
    const session = state.getIn(['edit', 'session']);
    const { signal } = profileListingsController.start();

    api.profiles
      .getListings({ limit, offset, profileId, signal, type: profile.get('type') })
      .then(function onFulfilled(response) {
        dispatch(actions.edit.getProfileListings.fulfilled({ page, session, shopId, response }));
      })
      .catch(function onRejected(error) {
        dispatch(actions.edit.getProfileListings.rejected({ error, session, shopId, signal }));
      });
  };

  return state.setIn(['edit', 'products', shopId, 'profile', 'listings', 'loading'], true);
}

function* getProfileListingsFulfilled(state, { page, session, response, shopId }) {
  if (state.getIn(['edit', 'session']) !== session || !state.hasIn(['edit', 'products', shopId, 'profile'])) {
    return state;
  }

  abort(profileListingsController);

  function updateListings(listings) {
    return shapeListingsForApp({ response, state: listings.delete('loading').set('page', page) });
  }

  return state.updateIn(['edit', 'products', shopId, 'profile', 'listings'], updateListings);
}

function* getProfileListingsRejected(state, { error, session, shopId, signal }) {
  if (
    signal.aborted ||
    state.getIn(['edit', 'session']) !== session ||
    !state.hasIn(['edit', 'products', shopId, 'profile'])
  ) {
    return state;
  }

  abort(profileListingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return state.deleteIn(['edit', 'products', shopId, 'profile', 'listings', 'loading']);
}

function* removeNew(reduction, shopId) {
  let data = reduction.get('edit');

  function filterShopIds(item) {
    return item !== shopId;
  }

  function removeFromShops(shops) {
    return shops.filter(filterShopIds);
  }

  function removeByShopId(items = EMPTY.MAP) {
    return items.delete(shopId);
  }

  data = data
    .update('shops', removeFromShops)
    .update('products', removeByShopId)
    .update('statuses', removeByShopId);

  if (data.get('activeShopId') === shopId) {
    data = data.set('activeShopId', data.get('shopId'));
  }

  return reduction.set('edit', data.set('isComplete', areComplete(data.get('products'))));
}

function* saveAs(reduction, { scheduleAt, statuses }) {
  let data = reduction.get('edit');

  if (!data.get('syncStarted')) {
    const shopsData = reduction.getIn(['data', 'shopsData'], EMPTY.MAP);
    const userShops = reduction.get('shops');
    const { canChange, data: updatedData, indicators, modal } = canChangeStatuses({
      data,
      scheduleAt,
      shopsData,
      statuses,
      userShops,
    });

    if (updatedData) {
      data = updatedData;
    }

    if (!canChange) {
      if (modal) {
        yield function effect(dispatch) {
          dispatch(actions.edit.setModal(modal));
        };
      }

      return reduction.set('edit', data);
    }

    yield function effect(dispatch) {
      function setSync({ shopId, ...syncData }) {
        dispatch(
          actions.shops.setData({
            path: ['syncData', shopId],
            value: Map(syncData),
          })
        );
      }

      indicators.forEach(setSync);

      dispatch(actions.edit.saveAs({ scheduleAt, statuses }));
    };

    if (!getSize(data.get('processing'))) {
      data = data
        .set('products', EMPTY.MAP)
        .set('processing', data);
    }

    return reduction.set('edit', data.set('syncStarted', true));
  } else if (data.get('session') === data.getIn(['processing', 'session']) && data.has('modal')) {
    data = data.delete('modal');
  }

  yield function effect(dispatch) {
    if (data.get('productId') === NEW) {
      dispatch(actions.edit.saveNew({ scheduleAt, statuses }));
    } else {
      dispatch(actions.edit.saveToChannels({ scheduleAt, statuses }));
    }
  };

  return reduction.set('edit', data.delete('syncStarted'));
}

function* saveNew(reduction, { scheduleAt, statuses }) {
  yield function effect(dispatch) {
    dispatch(
      actions.shops.rescheduleShopsPolling(
        reduction.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
        POLLING_INTERVAL.SHORT
      )
    );

    const data = reduction.getIn(['edit', 'processing']);
    const isLinkedProduct = getSize(data.get('shops')) > 1;
    const shopId = data.get('shopId');
    const db = reduction.getIn(['shops', 'byId', shopId, 'db']);
    const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
    const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
    const request = { params: { db }};

    if (isLinkedProduct) {
      const userShops = reduction.get('shops');
      request.payload = shapeNewLinkedProductsForAPI({ data, scheduleAt, statuses, userFeatures, userShops });
      request.url = `/shop/${shopId}/${ENDPOINTS.CREATE.LINKED}`;
    } else {
      const status = statuses.get(shopId);
      const product = data.getIn(['products', shopId]);
      request.payload = {
        saveOnly: isVelaStatus({ channel, status }),
        operations: shapeNewProductForAPI({ product, shopId, status, scheduleAt, userFeatures }),
      };

      request.url = `/shops/${shopId}/${ENDPOINTS.CREATE[channel]}`;

      if (product.has('profile')) {
        request.payload = addProfileId({ operation: request.payload, product });
      }
    }

    api
      .post(request)
      .then(
        (response) => dispatch(actions.edit.saveNew.fulfilled({ channel, response, shopId, statuses })),
        (error) => dispatch(actions.edit.saveNew.rejected({ error }))
      );
  };

  return reduction;
}

function* saveNewFulfilled(reduction, { response, shopId, statuses }) {
  switch (response?.result) {
    case 'ok':
    case 'succeeded': {
      yield function effect(dispatch) {
        const status = statuses.get(shopId);

        if (status === STATUS[VELA].SCHEDULED) {
          dispatch(
            actions.notifications.add({
              type: NOTIFICATION.SUCCESS,
              message: MESSAGE.SUCCESS.SCHEDULE_PUBLICATION,
            })
          );

          if (reduction.getIn(['edit', 'session']) === reduction.getIn(['edit', 'processing', 'session'])) {
            dispatch(actions.edit.setData({ status }));
          }
        } else {
          if (status !== STATUS[VELA].STAGING) {
            dispatch(
              actions.edit.addPlaceholdersForNewListings({
                data: reduction.getIn(['edit', 'processing']),
                shopIds: reduction.getIn(['edit', 'processing', 'shops']),
                status,
              })
            );
          }

          if (reduction.getIn(['edit', 'session']) === reduction.getIn(['edit', 'processing', 'session'])) {
            navigateTo(getListingsPath(reduction.setIn(['listings', 'status'], status)));
          }
        }
      };

      if (reduction.getIn(['edit', 'session']) === reduction.getIn(['edit', 'processing', 'session'])) {
        return reduction
          .deleteIn(['edit', 'processing'])
          .setIn(['edit', 'reloadAfterSave'], true);
      }

      return reduction.deleteIn(['edit', 'processing']);
    }

    default: {
      yield function effect(dispatch) {
        dispatch(actions.edit.saveNew.rejected({ error: response?.error }));
      };

      return reduction;
    }
  }
}

function* saveNewRejected(reduction, { error }) {
  yield function effect(dispatch) {
    function removeSyncIndicator(shopId) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    console.error(error || 'Unknown error encountered when posting new listing');

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_LISTINGS,
      })
    );

    reduction.getIn(['edit', 'processing', 'shops'], EMPTY.LIST).forEach(removeSyncIndicator);
  };

  if (reduction.getIn(['edit', 'session']) === reduction.getIn(['edit', 'processing', 'session'])) {
    const data = reduction.getIn(['edit', 'processing']);
    return reduction.set('edit', data.set('products', data.get('products').map(filterStatusAndScheduleOperations)));
  }

  return reduction.deleteIn(['edit', 'processing']);
}

function* saveToChannels(reduction, { scheduleAt, statuses }) {
  yield function effect(dispatch) {
    const userShops = reduction.get('shops');

    function reduceLinkedProducts(result, product) {
      result.push(shapeLinkedProductForAPI({ product, userShops }));
      return result;
    }

    function removeSyncIndicator(shopId) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    const data = reduction.getIn(['edit', 'processing']);
    const shopId = data.get('shopId');
    const shop = userShops.getIn(['byId', shopId]);
    const channel = shop.get('channel');
    const db = shop.get('db');
    const shops = data.get('shops');
    const isLinkedProduct = getSize(shops) > 1;
    const schedule = scheduleAt !== undefined && canBeScheduled(data.getIn(['products', shopId]));
    const scheduleAction = schedule
      ? scheduleAt
        ? data.getIn(['products', shopId, 'scheduledAt'])
          ? SCHEDULE_ACTION.RESCHEDULE
          : SCHEDULE_ACTION.SCHEDULE
        : SCHEDULE_ACTION.CANCEL
      : undefined;

    dispatch(
      actions.shops.rescheduleShopsPolling(
        reduction.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
        POLLING_INTERVAL.SHORT
      )
    );

    if (isLinkedProduct) {
      const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
      const linkId = data.get('linkId');
      const payload = {
        db,
        linkId,
        shopId,
        ...shapeLinkedProductsOperations({
          linkId,
          products: data.get('products'),
          scheduleAt,
          shopId,
          statuses,
          userFeatures,
          userShops,
        }),
      };

      if (!getSize(payload.newProducts) && !getSize(payload.productsToEdit)) {
        data.get('shops').forEach(removeSyncIndicator);
        dispatch(actions.edit.getProduct());
        return;
      }

      if (getSize(payload.newProducts)) {
        payload.existingProducts = data
          .get('linkedProducts', EMPTY.LIST)
          .reduce(
            reduceLinkedProducts,
            [shapeLinkedProductForAPI({ product: data.getIn(['products', shopId]), userShops })],
          );
      }

      data.get('products').forEach(function forEachProduct(product, key) {
        const productId = product.get('productId');
        const status = statuses.get(key);

        if (status !== STATUS[VELA].SCHEDULED && getSize(product.get('operations'))) {
          dispatch(actions.listings.toggleState({ listings: [productId], state: ['processing', key] }));

          if (shouldAddPlaceholder({ product, status })) {
            dispatch(actions.edit.addPlaceholdersForNewListings({ data, shopIds: [key], status }));
          }
        }

        if (productId !== NEW && product.has('profile')) {
          const profileId = product.getIn(['profile', 'id']);
          const profileType = product.getIn(['profile', 'type']);

          api.profiles
            .connectListings({ listings: [productId], profileId, type: profileType })
            .then(() => dispatch(actions.data.getProfiles({ force: true, shopId: key, type: profileType })))
            .catch((error) => {
              console.error(error);
              dispatch(
                actions.notifications.add({
                  type: NOTIFICATION.ERROR,
                  message: MESSAGE.FAIL.CONNECT_PROFILE,
                })
              );
            });
        }
      });

      api.products
        .editLinkedProducts(payload)
        .then(
          (response) => {
            dispatch(
              actions.edit.saveToChannels.fulfilled({
                response,
                scheduleAction,
                status: statuses.get(shopId),
              })
            );
          },
          (error) => dispatch(actions.edit.saveToChannels.rejected({ error, scheduleAction }))
        );
    } else {
      const product = data.getIn(['products', shopId]);
      const operations = shapeProductOperations({ product, scheduleAt });

      if (!getSize(operations)) {
        dispatch(actions.edit.getProduct());
        removeSyncIndicator(shopId);
        return;
      }

      const productId = product.get('productId');
      const status = statuses.get(shopId);

      if (status !== STATUS[VELA].SCHEDULED) {
        dispatch(actions.listings.toggleState({ listings: [productId], state: ['processing', shopId] }));

        if (shouldAddPlaceholder({ product, status })) {
          dispatch(actions.edit.addPlaceholdersForNewListings({ data, shopIds: [shopId], status }));
        }
      }

      if (productId !== NEW && product.has('profile')) {
        const profileId = product.getIn(['profile', 'id']);
        const profileType = product.getIn(['profile', 'type']);
        api.profiles
          .connectListings({ listings: [productId], profileId, type: profileType })
          .then(() => dispatch(actions.data.getProfiles({ force: true, shopId, type: profileType })))
          .catch((error) => {
            console.error(error);
            dispatch(
              actions.notifications.add({
                type: NOTIFICATION.ERROR,
                message: MESSAGE.FAIL.CONNECT_PROFILE,
              })
            );
          });
      }

      api.products
        .editSingleProduct({ channel, db, operations, shopId, status })
        .then(
          () => dispatch(actions.edit.saveToChannels.fulfilled({ scheduleAction, status })),
          (error) => dispatch(actions.edit.saveToChannels.rejected({ error, scheduleAction }))
        );
    }
  };

  return reduction;
}

function* saveToChannelsFulfilled(reduction, { response, scheduleAction, status }) {
  if (scheduleAction) {
    yield function effect(dispatch) {
      const actionMessage = {
        [SCHEDULE_ACTION.RESCHEDULE]: MESSAGE.SUCCESS.RESCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.SCHEDULE]: MESSAGE.SUCCESS.SCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.CANCEL]: MESSAGE.SUCCESS.CANCEL_PUBLICATION,
      };

      dispatch(actions.notifications.add({ type: NOTIFICATION.SUCCESS, message: actionMessage[scheduleAction] }));
    };
  }

  if (reduction.getIn(['edit', 'session']) === reduction.getIn(['edit', 'processing', 'session'])) {
    yield function effect(dispatch) {
      if (scheduleAction !== SCHEDULE_ACTION.CANCEL || status !== STATUS[VELA].SCHEDULED) {
        if (response?.linkId) {
          dispatch(actions.edit.setData({ linkId: shapeId(response.linkId) }));
        }

        dispatch(actions.edit.setData({ loading: true }));
      } else {
        dispatch(actions.edit.setData({ cancel: true }));
      }
    };

    return reduction
      .deleteIn(['edit', 'processing'])
      .setIn(['edit', 'reloadAfterSave'], true);
  }

  return reduction.deleteIn(['edit', 'processing']);
}

function* saveToChannelsRejected(reduction, { error, scheduleAction }) {
  yield function effect(dispatch) {
    function removeSyncIndicator(shopId) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    console.error('Error encountered while syncing updates: ', error);

    reduction.getIn(['edit', 'processing', 'shops'], EMPTY.LIST).forEach(removeSyncIndicator);

    if (scheduleAction) {
      const actionMessage = {
        [SCHEDULE_ACTION.RESCHEDULE]: MESSAGE.FAIL.RESCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.SCHEDULE]: MESSAGE.FAIL.SCHEDULE_PUBLICATION,
        [SCHEDULE_ACTION.CANCEL]: MESSAGE.FAIL.CANCEL_PUBLICATION,
      };

      dispatch(actions.notifications.add({ type: NOTIFICATION.ERROR, message: actionMessage[scheduleAction] }));
    } else {
      dispatch(actions.notifications.add({ message: MESSAGE.FAIL.SYNC, type: NOTIFICATION.ERROR }));
    }
  };

  if (reduction.getIn(['edit', 'session']) === reduction.getIn(['edit', 'processing', 'session'])) {
    const data = reduction.getIn(['edit', 'processing']);
    return reduction.set('edit', data.set('products', data.get('products').map(filterStatusAndScheduleOperations)));
  }

  return reduction.deleteIn(['edit', 'processing']);
}

function* setData(reduction, data) {
  let state = reduction;

  for (const field in data) {
    if (data.hasOwnProperty(field)) {
      state = state.setIn(['edit', field], data[field]);
    }
  }

  return state;
}

function* setModal(reduction, payload) {
  return payload
    ? reduction.setIn(['edit', 'modal'], Map(payload))
    : reduction.deleteIn(['edit', 'modal']);
}

function* setProduct(reduction, { product, shopId }) {
  const edit = reduction.get('edit').setIn(['products', shopId], product);

  return reduction.set('edit', edit.set('isComplete', areComplete(edit.get('products'))));
}

function* setProfile(reduction, payload) {
  abort(profileController);
  abort(profileListingsController);

  const { profile, shopId } = payload;

  if (!profile) {
    return reduction.setIn(['edit', 'products', shopId], reduction.getIn(['edit', 'products', shopId, 'oldProduct']));
  }

  const type = profile.get('type');

  switch (type) {
    case PROFILE_TYPE.AI: {
      yield function effect(dispatch) {
        const session = reduction.getIn(['edit', 'session']);
        const { signal } = profileController.start();

        api.profiles
          .getById({ profileId: profile.get('id'), signal, type })
          .then(
            (response) => dispatch(actions.edit.setProfile.fulfilled({ ...payload, response, session, type })),
            (error) => dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal })),
          );
      };

      break;
    }

    case PROFILE_TYPE.STANDARD: {
      yield function effect(dispatch) {
        const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
        const db = reduction.getIn(['shops', 'byId', shopId, 'db']);
        const productId = profile.get('templateId');
        const { signal } = profileController.start();
        dispatch(actions.edit.getProductData({ asProfile: true, channel, db, productId, shopId, signal }));
      };

      break;
    }

    default: {
      return reduction;
    }
  }

  let product = reduction.getIn(['edit', 'products', shopId]);

  if (!product.has('oldProduct')) {
    product = product.set('oldProduct', product);
  }

  return reduction.setIn(['edit', 'products', shopId], product.set('loading', true).set('profile', profile));
}

function* setProfileFulfilled(reduction, { session, shopId, type, ...payload }) {
  if (reduction.getIn(['edit', 'session']) !== session) {
    return reduction;
  }

  abort(profileController);

  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const channel = reduction.getIn(['shops', 'byId', shopId, 'channel']);
  const marketplaceId = reduction.getIn(['shops', 'byId', shopId, 'marketplaceId']);
  const titleFormatPreference = reduction.getIn(['shops', 'byId', shopId, 'titleFormatPreference']);
  const taxonomyData = get([channel, marketplaceId])(DATA);
  const edit = reduction.get('edit');

  function clearCategoriesToLoad(state) {
    const categoriesToLoad = state.get('waitingForCategoriesToLoad', EMPTY.MAP).delete(shopId);

    return getSize(categoriesToLoad) > 0
      ? state.set('waitingForCategoriesToLoad', categoriesToLoad)
      : state.delete('waitingForCategoriesToLoad');
  }

  switch (type) {
    case PROFILE_TYPE.AI: {
      const oldProduct = reduction.getIn(['edit', 'products', shopId, 'oldProduct']);
      const profile = payload.profile.set('ai', shapeAiOperationsForApp(payload.response.data));
      const applied = applyProfile({
        product: oldProduct,
        profile,
        shopData,
        taxonomyData,
        titleFormatPreference,
        type,
        userFeatures,
      });
      applied.product = applied.product.set('oldProduct', oldProduct).set('profile', profile);
      return reduction.set('edit', edit.setIn(['products', shopId], applied.product));
    }

    case PROFILE_TYPE.STANDARD: {
      const profile = edit.getIn(['products', shopId, 'profile']);

      if (!profile) {
        return reduction;
      }

      const oldProduct = edit.getIn(['products', shopId, 'oldProduct']);
      const applied = payload.applied || applyProfile({
        product: oldProduct,
        profile,
        shopData,
        taxonomyData,
        template: profile.get('template'),
        titleFormatPreference,
        type: PROFILE_TYPE.STANDARD,
        userFeatures,
      });

      applied.product = applied.product.set('oldProduct', oldProduct).set('profile', profile);

      if (applied.confirmation) {
        yield function effect(dispatch) {
          dispatch(
            actions.edit.setModal({
              product: applied.product,
              restore: true,
              shopId,
              type: applied.confirmation,
            })
          );
        };

        return reduction;
      }

      if (
        !payload.applied &&
        channel === SHOPIFY &&
        oldProduct.has('keywords') &&
        applied.product.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text']) &&
        applied.product.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text']) !== oldProduct.getIn([FORM.PHOTOS, VALUE, 0, 'alt_text'])
      ) {
        yield function effect(dispatch) {
          function onResponse(value) {
            const updates = editKeywords({ product: applied.product, value });
            updates.product = updateListingScore(
              updates.product.set('operations',
                updates.product.get('operations', EMPTY.LIST).concat(updates.operations)
              ),
              { titleFormatPreference }
            );

            dispatch(
              actions.edit.setProfile.fulfilled({
                applied: { ...applied, product: updates.product },
                session,
                shopId,
                signal: payload.signal,
                type: PROFILE_TYPE.STANDARD,
              })
            );
          }

          function onFail(error) {
            dispatch(actions.edit.setProfile.rejected({ error, session, shopId, signal: payload.signal }));
          }

          generateKeywords({
            dispatch,
            product: applied.product,
            shop: Map({
              db: reduction.getIn(['shops', 'byId', shopId, 'db']),
              name: reduction.getIn(['shops', 'byId', shopId, 'name']),
              shopId,
              titleFormatPreference: reduction.getIn(['shops', 'byId', shopId, 'titleFormatPreference']),
            }),
          })
            .then(onResponse)
            .catch(onFail);
        };

        return reduction;
      }

      const products = edit.get('products').set(shopId, applied.product);
      return reduction.set('edit',
        clearCategoriesToLoad(
          edit
            .set('products', products)
            .set('isComplete', areComplete(products))
        )
      );
    }

    default: {
      return reduction;
    }
  }
}

function* setProfileRejected(reduction, { error, session, shopId, signal }) {
  if (signal.aborted || reduction.getIn(['edit', 'session']) !== session) return reduction;

  abort(profileController);

  yield function effect(dispatch) {
    console.error('Error encountered while getting profile: ', error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_PROFILE,
      })
    );
  };

  return reduction.setIn(['edit', 'products', shopId], reduction.getIn(['edit', 'products', shopId, 'oldProduct']));
}

function* toggleShop(reduction, shopId) {
  const edit = reduction.get('edit');
  const isNew = edit.get('productId') === NEW;
  const connected = edit.hasIn(['products', shopId]);
  const targetShop = reduction.getIn(['shops', 'byId', shopId]);

  yield function effect(dispatch) {
    function openModal() {
      dispatch(actions.edit.setModal({ type: MODALS.COPY_CONTENT, targetShop }));
    }

    if (isNew) {
      if (connected) {
        dispatch(actions.edit.removeNew(shopId));
      } else if (getSize(edit.get('products'))) {
        openModal();
      } else {
        dispatch(actions.edit.createNew({ shopId }));
      }

      return;
    }

    if (connected) {
      if (edit.getIn(['products', shopId, 'productId']) === NEW) {
        dispatch(actions.edit.removeNew(shopId));
      }
    } else if (getSize(edit.get('products'))) {
      openModal();
    } else {
      dispatch(actions.edit.createNew({ shopId }));
    }
  };

  return reduction;
}

function* updateProductAttributes(state, { shopId }) {
  yield function effect(dispatch) {
    const edit = state.get('edit', EMPTY.MAP);

    if (!edit.hasIn(['products', shopId])) return;

    let product = edit.getIn(['products', shopId]).delete('loading');
    const channel = product.get('channel');
    const shopData = state.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
    const marketplaceId = state.getIn(['shops', 'byId', shopId, 'marketplaceId']);
    const taxonomyData = get([channel, marketplaceId])(DATA);
    const category = channel === EBAY
      ? product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE])
      : product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);

    const itemSpecifics = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);

    product = product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS],
      updateItemSpecificsForCategory({ category, channel, itemSpecifics, taxonomyData })
    );

    const productDetails = product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP);

    product = product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS],
      updateProductDetailsForCategory({ category, channel, productDetails, taxonomyData })
    );

    dispatch(actions.edit.setProduct({ shopId, product: setProductErrors({ product, shopData, taxonomyData }) }));
  };

  return state;
}

const edit = {
  [actions.edit.addPlaceholdersForNewListings]: addPlaceholdersForNewListings,
  [actions.edit.cancelEdit]: cancelEdit,
  [actions.edit.createNew]: createNew,
  [actions.edit.getData]: getData,
  [actions.edit.getLinkedProducts]: getLinkedProducts,
  [actions.edit.getLinkedProducts.fulfilled]: getLinkedProductsFulfilled,
  [actions.edit.getLinkedProducts.rejected]: getLinkedProductsRejected,
  [actions.edit.getProduct]: getProduct,
  [actions.edit.getProductData]: getProductData,
  [actions.edit.getProductData.fulfilled]: getProductDataFulfilled,
  [actions.edit.getProductData.rejected]: getProductDataRejected,
  [actions.edit.getProfileListings]: getProfileListings,
  [actions.edit.getProfileListings.fulfilled]: getProfileListingsFulfilled,
  [actions.edit.getProfileListings.rejected]: getProfileListingsRejected,
  [actions.edit.removeNew]: removeNew,
  [actions.edit.saveAs]: saveAs,
  [actions.edit.saveNew]: saveNew,
  [actions.edit.saveNew.rejected]: saveNewRejected,
  [actions.edit.saveNew.fulfilled]: saveNewFulfilled,
  [actions.edit.saveToChannels]: saveToChannels,
  [actions.edit.saveToChannels.fulfilled]: saveToChannelsFulfilled,
  [actions.edit.saveToChannels.rejected]: saveToChannelsRejected,
  [actions.edit.setData]: setData,
  [actions.edit.setModal]: setModal,
  [actions.edit.setProduct]: setProduct,
  [actions.edit.setProfile]: setProfile,
  [actions.edit.setProfile.fulfilled]: setProfileFulfilled,
  [actions.edit.setProfile.rejected]: setProfileRejected,
  [actions.edit.toggleShop]: toggleShop,
  [actions.edit.updateProductAttributes]: updateProductAttributes,
};

export default edit;
